import { Component, Input, OnInit, inject } from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { DashboardComponent } from "src/app/real-estate/BackOffice/dashboard/dashboard.component";

interface WalkthroughHelpPoint {
  icon: string;
  title: string;
  description: string;
  iconClass?: string;
}

interface WalkthroughVideoConfig {
  title: string;
  subtitle: string;
  youtubeVideoId: string;
  helpTitle: string;
  helpPoints: WalkthroughHelpPoint[];
  tip?: string;
}

@Component({
  selector: "app-walkthrough-video",
  templateUrl: "./walkthrough-video.component.html",
  styleUrls: ["./walkthrough-video.component.css"],
})
export class WalkthroughVideoComponent implements OnInit {
  private _helpKey: string = "school-dashboard";

  @Input()
  set helpKey(value: string) {
    this._helpKey = value || "school-dashboard";
    this.applyWalkthroughConfig();
  }

  get helpKey(): string {
    return this._helpKey;
  }

  private sanitizer = inject(DomSanitizer);
  activeModal = inject(NgbActiveModal);

  selectedConfig!: WalkthroughVideoConfig;
  safeYoutubeUrl!: SafeResourceUrl;

  private walkthroughVideoMap: Record<string, WalkthroughVideoConfig> = {
    // Admin Dashboard Video link
    "school-dashboard": {
      title: "School Dashboard Walkthrough",
      subtitle: "Learn how to use this dashboard and understand each section.",
      youtubeVideoId: "L84PHS28dTg",
      helpTitle: "What this dashboard shows",
      helpPoints: [
        {
          icon: "fa-solid fa-graduation-cap",
          title: "Classes Running",
          description:
            "Shows active classes configured for the selected school.",
          iconClass: "bg-success-subtle",
        },
        {
          icon: "fa-solid fa-users",
          title: "Staff Attendance",
          description: "Shows monthly staff attendance percentage.",
          iconClass: "bg-warning-subtle",
        },
        {
          icon: "bi bi-file-earmark-text-fill",
          title: "Admissions",
          description:
            "Shows pending and approved admissions for selected month.",
          iconClass: "bg-info-subtle",
        },
        {
          icon: "bi bi-clock-history",
          title: "Attendance Tracker",
          description:
            "Used for punch in, punch out, and today’s worked hours.",
          iconClass: "bg-danger-subtle",
        },
        {
          icon: "bi bi-chat-dots-fill",
          title: "Discussion",
          description: "Shows recent conversations and unread message count.",
          iconClass: "bg-light",
        },
        {
          icon: "bi bi-cash-coin",
          title: "Fees & Expenses",
          description:
            "Shows fee plans, pending fees, collections, and school expenses.",
          iconClass: "bg-light",
        },
        {
          icon: "bi bi-ticket-detailed-fill",
          title: "Ticket Status",
          description: "Shows open and in-progress support tickets.",
          iconClass: "bg-light",
        },
        {
          icon: "bi bi-megaphone-fill",
          title: "Communication Hubs",
          description: "Shows latest announcements and school communications.",
          iconClass: "bg-light",
        },
      ],
      tip: "Use month and year filters to refresh dashboard values.",
    },
// ============= Teacher Dashboard ==============
    "teacher-dashboard": {
  title: "Teacher Dashboard Walkthrough",
  subtitle: "Learn how to use the teacher dashboard and understand each section.",
  youtubeVideoId: "kW7pkU4YVU",
  helpTitle: "What this teacher dashboard shows",
  helpPoints: [
    {
      icon: "fa-solid fa-user-graduate",
      title: "Total Students",
      description: "Shows the total students available for the teacher’s mapped class and section.",
      iconClass: "bg-primary-subtle",
    },
    {
      icon: "fa-solid fa-venus-mars",
      title: "Boys : Girls Ratio",
      description: "Shows gender ratio for the selected class and section.",
      iconClass: "bg-success-subtle",
    },
    {
      icon: "fa-solid fa-user-check",
      title: "Average Attendance",
      description: "Shows average class attendance for the selected month and year.",
      iconClass: "bg-warning-subtle",
    },
    {
      icon: "fa-solid fa-chart-line",
      title: "Academic Score",
      description: "Shows overall academic score percentage for the selected class.",
      iconClass: "bg-danger-subtle",
    },
    {
      icon: "bi bi-clock-history",
      title: "Attendance Tracker",
      description: "Used by the teacher for punch in, punch out, and today’s worked hours.",
      iconClass: "bg-info-subtle",
    },
    {
      icon: "bi bi-chat-dots-fill",
      title: "Discussion",
      description: "Shows recent conversations and unread message count.",
      iconClass: "bg-light",
    },
    {
      icon: "bi bi-bell-fill",
      title: "Notifications",
      description: "Shows recent announcements and important updates.",
      iconClass: "bg-light",
    },
    {
      icon: "bi bi-journal-check",
      title: "Assignment Progress",
      description: "Shows assignment submission progress, pending count, and completed count.",
      iconClass: "bg-success-subtle",
    },
    {
      icon: "bi bi-calendar-event-fill",
      title: "Upcoming Events",
      description: "Shows upcoming school events for the teacher.",
      iconClass: "bg-warning-subtle",
    },
  ],
  tip: "Use month and year filters to refresh attendance, academic, and dashboard values.",
},

"schedule-planner": {
  title: "Schedule Planner Walkthrough",
  subtitle: "Learn how to create, auto-generate, manage, and download class timetables.",
  youtubeVideoId: "JfvvaI3fj5U",
  helpTitle: "How Schedule Planner works",
  helpPoints: [
    {
      icon: "bi bi-calendar-week-fill",
      title: "Timetable Grid",
      description: "Shows weekly timetable slots from Monday to Saturday with subject, teacher, class, and section details.",
      iconClass: "bg-primary-subtle",
    },
    {
      icon: "fa-solid fa-chalkboard-user",
      title: "Teacher Filter",
      description: "Select a teacher to view or manage that teacher’s timetable.",
      iconClass: "bg-info-subtle",
    },
    {
      icon: "fa-solid fa-school",
      title: "Class Filter",
      description: "Filter timetable records by class to review class-wise lecture planning.",
      iconClass: "bg-success-subtle",
    },
    {
      icon: "bi bi-magic",
      title: "Auto Generate Timetable",
      description: "Generate timetable automatically using academic year, class, section, school timing, period duration, break time, and working days.",
      iconClass: "bg-warning-subtle",
    },
    {
      icon: "bi bi-plus-circle-fill",
      title: "Add / Edit Schedule",
      description: "Click an available slot to add a lecture or click an existing schedule card to edit it.",
      iconClass: "bg-light",
    },
    {
      icon: "fa-solid fa-file-excel",
      title: "Download Excel",
      description: "Download the generated timetable and weekly teacher lecture summary in Excel format.",
      iconClass: "bg-success-subtle",
    },
    {
      icon: "bi bi-trash-fill",
      title: "Delete Class Timetable",
      description: "Delete the complete timetable for the selected class when needed.",
      iconClass: "bg-danger-subtle",
    },
  ],
  tip: "Use Auto Generate Timetable for faster planning, then review the weekly summary before finalizing.",
},

"teacher-assignments": {
  title: "Assignment Review Walkthrough",
  subtitle: "Learn how to view assignments and review student submissions.",
  youtubeVideoId: "BCYvIIWl6UM",
  helpTitle: "How Assignment Review works",
  helpPoints: [
    {
      icon: "bi bi-journal-text",
      title: "My Assignments",
      description: "Shows all assignments created or assigned by the logged-in teacher.",
      iconClass: "bg-primary-subtle",
    },
    {
      icon: "fa-solid fa-chalkboard-user",
      title: "Class & Section",
      description: "Displays the class and section linked with each assignment.",
      iconClass: "bg-info-subtle",
    },
    {
      icon: "fa-solid fa-book-open",
      title: "Subject",
      description: "Shows the subject for which the assignment was created.",
      iconClass: "bg-success-subtle",
    },
    {
      icon: "bi bi-calendar-check-fill",
      title: "Assigned & Due Date",
      description: "Shows when the assignment was assigned and the last submission date.",
      iconClass: "bg-warning-subtle",
    },
    {
      icon: "bi bi-award-fill",
      title: "Total Marks",
      description: "Displays the maximum marks configured for the assignment.",
      iconClass: "bg-danger-subtle",
    },
    {
      icon: "bi bi-eye-fill",
      title: "View Submissions",
      description: "Open student submission history for checking and reviewing submitted work.",
      iconClass: "bg-light",
    },
  ],
  tip: "Click View Submissions to review student work for the selected assignment.",
},

// ============ student Admision =============

"class-attendance": {
  title: "Class Attendance Walkthrough",
  subtitle: "Learn how to mark, update, and review student attendance.",
  youtubeVideoId: "R1PZc40WtpU",
  helpTitle: "How Class Attendance works",
  helpPoints: [
    {
      icon: "bi bi-calendar-check-fill",
      title: "Select Attendance Date",
      description: "Use the date picker to view or mark attendance for the selected day.",
      iconClass: "bg-info-subtle",
    },
    {
      icon: "fa-solid fa-users",
      title: "Student List",
      description: "Shows all students mapped to the selected class and section.",
      iconClass: "bg-primary-subtle",
    },
    {
      icon: "bi bi-toggle-on",
      title: "Present / Absent Toggle",
      description: "Switch each student between Present and Absent before submitting.",
      iconClass: "bg-success-subtle",
    },
    {
      icon: "bi bi-check-circle-fill",
      title: "Submit Attendance",
      description: "Saves attendance for the selected class and date.",
      iconClass: "bg-warning-subtle",
    },
    {
      icon: "bi bi-pencil-square",
      title: "Update Attendance",
      description: "If attendance is already submitted, you can update it after confirmation.",
      iconClass: "bg-danger-subtle",
    },
  ],
  tip: "For past dates, attendance opens in view mode. Today’s attendance can be submitted or updated based on permission.",
},

// ===================== Certificate and kudos ============

"certificates-kudos": {
  title: "Certificates & Kudos Walkthrough",
  subtitle: "Learn how to issue certificates, send kudos, preview, and download recognition records.",
  youtubeVideoId: "R8sbDTNeNDI",
  helpTitle: "How Certificates & Kudos works",
  helpPoints: [
    {
      icon: "fa-solid fa-user-graduate",
      title: "Student Selection",
      description: "Select class, section, and student before generating a certificate or kudos.",
      iconClass: "bg-primary-subtle",
    },
    {
      icon: "fas fa-certificate",
      title: "Official Certificate",
      description: "Use this tab to issue achievement certificates with award title and category.",
      iconClass: "bg-warning-subtle",
    },
    {
      icon: "bi bi-file-earmark-text-fill",
      title: "Leaving Certificate",
      description: "Use this tab to generate leaving certificate details when available for the role.",
      iconClass: "bg-info-subtle",
    },
    {
      icon: "bi bi-stars",
      title: "Kudos",
      description: "Use this tab to send appreciation messages or recognition cards to students.",
      iconClass: "bg-success-subtle",
    },
    {
      icon: "bi bi-eye-fill",
      title: "Live Preview",
      description: "Preview the generated certificate or kudos before downloading.",
      iconClass: "bg-light",
    },
    {
      icon: "bi bi-download",
      title: "Download",
      description: "Download the generated certificate, leaving certificate, or kudos file.",
      iconClass: "bg-danger-subtle",
    },
  ],
  tip: "First select the student, then choose Certificate, Leaving Certificate, or Kudos and generate the preview.",
},

// ============ Exam shedule 

"exam-schedule": {
  title: "Exam Schedule Walkthrough",
  subtitle: "Learn how to view exam schedules, result status, and exam reports.",
  youtubeVideoId: "EcH9nM42TnA",
  helpTitle: "How Exam Schedule works",
  helpPoints: [
    {
      icon: "bi bi-calendar-event-fill",
      title: "Exam Schedule List",
      description: "Shows exam-wise schedule details such as date, class, section, subject, marks, and duration.",
      iconClass: "bg-primary-subtle",
    },
    {
      icon: "bi bi-calendar-check-fill",
      title: "Academic Year & Dates",
      description: "Displays the academic year, exam start date, and exam end date.",
      iconClass: "bg-info-subtle",
    },
    {
      icon: "bi bi-list-check",
      title: "Subject Details",
      description: "Each exam expands to show subject-wise exam date, total marks, and duration.",
      iconClass: "bg-success-subtle",
    },
    {
      icon: "bi bi-patch-check-fill",
      title: "Result Status",
      description: "Shows whether the result is published or not published for the selected exam.",
      iconClass: "bg-warning-subtle",
    },
    {
      icon: "bi bi-bar-chart-line-fill",
      title: "View Report",
      description: "If the result is published, student login can open and view the exam report.",
      iconClass: "bg-danger-subtle",
    },
  ],
  tip: "Open each exam accordion to view subject-wise schedule details. Published exams allow report viewing for student login.",
},

"class-weekly-timetable": {
  title: "Schedule Walkthrough",
  subtitle: "Learn how to view the weekly class timetable and understand each lecture slot.",
  youtubeVideoId: "aGj_zn8SFbs",
  helpTitle: "How Schedule works",
  helpPoints: [
    {
      icon: "bi bi-calendar-week-fill",
      title: "Weekly Timetable",
      description: "Shows the complete weekly timetable from Monday to Saturday.",
      iconClass: "bg-primary-subtle",
    },
    {
      icon: "bi bi-clock-fill",
      title: "Time Slots",
      description: "Each row represents a timetable time slot such as 07:00, 07:30, 08:00, and so on.",
      iconClass: "bg-info-subtle",
    },
    {
      icon: "fa-solid fa-book-open",
      title: "Subject Details",
      description: "Each lecture card shows the subject name assigned for that time slot.",
      iconClass: "bg-success-subtle",
    },
    {
      icon: "fa-solid fa-chalkboard-user",
      title: "Teacher Details",
      description: "Shows the teacher assigned to each subject or lecture.",
      iconClass: "bg-warning-subtle",
    },
    {
      icon: "bi bi-wifi",
      title: "Lecture Mode",
      description: "Shows whether the lecture is Online or Offline. Online lectures may include a Join link.",
      iconClass: "bg-danger-subtle",
    },
    {
      icon: "bi bi-filter-square-fill",
      title: "Section Filter",
      description: "Use the section dropdown to switch timetable view section-wise when available.",
      iconClass: "bg-light",
    },
  ],
  tip: "Use the section dropdown to view timetable for another section. Scroll inside the timetable to see all time slots.",
},

    "school-admission": {
      title: "Admission Module Walkthrough",
      subtitle: "Learn how to review, approve, and manage school admissions.",
      youtubeVideoId: "L84PHS28dTg",
      helpTitle: "What admission screen shows",
      helpPoints: [
        {
          icon: "bi bi-person-plus-fill",
          title: "New Admissions",
          description: "Shows newly submitted admission applications.",
          iconClass: "bg-info-subtle",
        },
        {
          icon: "bi bi-check-circle-fill",
          title: "Approved Admissions",
          description: "Shows admissions approved by school admin or owner.",
          iconClass: "bg-success-subtle",
        },
        {
          icon: "bi bi-hourglass-split",
          title: "Pending Admissions",
          description: "Shows applications waiting for review.",
          iconClass: "bg-warning-subtle",
        },
        {
          icon: "bi bi-x-circle-fill",
          title: "Rejected Admissions",
          description: "Shows applications rejected with reason.",
          iconClass: "bg-danger-subtle",
        },
      ],
      tip: "Use filters to quickly find admission records by status.",
    },

    "school-staff-master": {
      title: "Staff Master Walkthrough",
      subtitle:
        "Learn how to manage school owner, admin staff, teaching staff, non-teaching staff, and drivers.",
      youtubeVideoId: "L84PHS28dTg",
      helpTitle: "What this staff module shows",
      helpPoints: [
        {
          icon: "bi bi-person-badge-fill",
          title: "School Owner",
          description:
            "Use this tab to manage owner-level school users and their access.",
          iconClass: "bg-success-subtle",
        },
        {
          icon: "bi bi-person-gear",
          title: "Admin Staff",
          description:
            "Use this tab to manage school admin users who handle school operations.",
          iconClass: "bg-info-subtle",
        },
        {
          icon: "fa-solid fa-chalkboard-user",
          title: "Teaching Staff",
          description:
            "Use this tab to add, edit, and manage teachers and their details.",
          iconClass: "bg-warning-subtle",
        },
        {
          icon: "bi bi-people-fill",
          title: "Non-Teaching Staff",
          description:
            "Use this tab to manage non-teaching staff such as office, support, and operations staff.",
          iconClass: "bg-light",
        },
        {
          icon: "bi bi-bus-front-fill",
          title: "Drivers",
          description:
            "Use this tab to manage driver records and transport-related staff details.",
          iconClass: "bg-danger-subtle",
        },
        {
          icon: "bi bi-arrow-left-right",
          title: "Tabs",
          description:
            "Click any tab to switch between staff categories without leaving this screen.",
          iconClass: "bg-light",
        },
      ],
      tip: "Start with School Owner or Admin Staff, then add Teaching, Non-Teaching, and Driver records as required.",
    },

    "school-owner-form": {
      title: "Add School Owner Walkthrough",
      subtitle:
        "Learn how to fill owner personal details, address details, and login credentials.",
      youtubeVideoId: "L84PHS28dTg",
      helpTitle: "How to fill this form",
      helpPoints: [
        {
          icon: "bi bi-card-text",
          title: "Aadhaar & PAN",
          description:
            "Enter Aadhaar and PAN details if required by your school policy.",
          iconClass: "bg-info-subtle",
        },
        {
          icon: "bi bi-person-fill",
          title: "Profile Details",
          description:
            "Fill first name, middle name, last name, date of birth, gender, and profile image.",
          iconClass: "bg-success-subtle",
        },
        {
          icon: "bi bi-telephone-fill",
          title: "Contact Details",
          description:
            "Enter mobile number and email address carefully because they are used for communication.",
          iconClass: "bg-warning-subtle",
        },
        {
          icon: "bi bi-geo-alt-fill",
          title: "Address Details",
          description: "Add full address, pincode, country, state, and city.",
          iconClass: "bg-light",
        },
        {
          icon: "bi bi-shield-lock-fill",
          title: "Assign Credentials",
          description: "Create username and password for owner login access.",
          iconClass: "bg-danger-subtle",
        },
        {
          icon: "bi bi-check-circle-fill",
          title: "Submit",
          description:
            "After checking all required fields, click Submit to save the school owner.",
          iconClass: "bg-success-subtle",
        },
      ],
      tip: "Always verify mobile number, email, username, and password before submitting.",
    },

    "school-class-listing": {
      title: "Class Listing Walkthrough",
      subtitle:
        "Learn how to search, add, edit, delete, bulk upload, and manage school classes.",
      youtubeVideoId: "L84PHS28dTg",
      helpTitle: "What this class module shows",
      helpPoints: [
        {
          icon: "fa-solid fa-school",
          title: "Class Listing",
          description:
            "Shows all classes created for the selected school, including class name, education level, stream, and description.",
          iconClass: "bg-success-subtle",
        },
        {
          icon: "bi bi-search",
          title: "Search Class",
          description:
            "Use the search box to quickly find a class by class name.",
          iconClass: "bg-info-subtle",
        },
        {
          icon: "bi bi-plus-circle-fill",
          title: "Add Class",
          description:
            "Click Add Class to open the Register Class form and create a new class.",
          iconClass: "bg-warning-subtle",
        },
        {
          icon: "bi bi-pencil-fill",
          title: "Edit Class",
          description:
            "Use the action menu to update an existing class record.",
          iconClass: "bg-light",
        },
        {
          icon: "bi bi-trash-fill",
          title: "Delete Class",
          description:
            "Use the action menu to delete a class if it is no longer required.",
          iconClass: "bg-danger-subtle",
        },
        {
          icon: "bi bi-files",
          title: "Clone Fee Structure",
          description:
            "Clone fee structures from another class to speed up fee setup.",
          iconClass: "bg-light",
        },
        {
          icon: "bi bi-file-earmark-arrow-down-fill",
          title: "Download Excel Template",
          description:
            "Download the sample Excel format before bulk uploading class records.",
          iconClass: "bg-success-subtle",
        },
        {
          icon: "bi bi-file-earmark-arrow-up-fill",
          title: "Bulk Upload Classes",
          description:
            "Upload class records using Excel to create multiple classes faster.",
          iconClass: "bg-info-subtle",
        },
      ],
      tip: "Use Add Class for single entries and Excel upload when creating multiple classes together.",
    },

    "school-class-form": {
      title: "Register Class Walkthrough",
      subtitle:
        "Learn how to select a class, choose stream, add description, and submit class details.",
      youtubeVideoId: "L84PHS28dTg",
      helpTitle: "How to fill the Register Class form",
      helpPoints: [
        {
          icon: "bi bi-ui-checks-grid",
          title: "Quick Select Class",
          description:
            "Choose Nursery, LKG, UKG, Class 1 to Class 12, or Other from quick-select chips.",
          iconClass: "bg-success-subtle",
        },
        {
          icon: "bi bi-diagram-3-fill",
          title: "Stream Selection",
          description:
            "For Class 11 and Class 12, select the stream such as Science, Commerce, Arts, or Other.",
          iconClass: "bg-info-subtle",
        },
        {
          icon: "bi bi-pencil-square",
          title: "Custom Class",
          description:
            "Use Other when you want to create custom programs like Diploma, BCA, MBA, or ITI.",
          iconClass: "bg-warning-subtle",
        },
        {
          icon: "fa-solid fa-wand-magic-sparkles",
          title: "Generate with AI",
          description:
            "Use Generate with AI to create a short class description automatically.",
          iconClass: "bg-light",
        },
        {
          icon: "bi bi-eye-fill",
          title: "Preview",
          description:
            "Preview confirms the final class name and education level before saving.",
          iconClass: "bg-success-subtle",
        },
        {
          icon: "bi bi-check-circle-fill",
          title: "Submit",
          description:
            "Click Submit after selecting class details and confirming the preview.",
          iconClass: "bg-danger-subtle",
        },
      ],
      tip: "For regular school classes, use quick-select chips. Use Other only for custom courses or programs.",
    },
    "school-exam-listing": {
  title: "Exam Master Walkthrough",
  subtitle:
    "Learn how to search, add, edit, delete, bulk upload, and manage school exams.",
  youtubeVideoId: "L84PHS28dTg",
  helpTitle: "What this exam module shows",
  helpPoints: [
    {
      icon: "bi bi-journal-text",
      title: "Exam Master",
      description:
        "Shows all created exams with exam name, type, description, start date, end date, grace marks, and academic year.",
      iconClass: "bg-success-subtle",
    },
    {
      icon: "bi bi-search",
      title: "Search Exam",
      description:
        "Use the search box to quickly find exams by exam name.",
      iconClass: "bg-info-subtle",
    },
    {
      icon: "bi bi-plus-circle-fill",
      title: "Add New Exam",
      description:
        "Click Add New Exam to open the Create Exam form and create a new exam.",
      iconClass: "bg-warning-subtle",
    },
    {
      icon: "bi bi-pencil-fill",
      title: "Edit Exam",
      description:
        "Use the action menu to update an existing exam record.",
      iconClass: "bg-light",
    },
    {
      icon: "bi bi-trash-fill",
      title: "Delete Exam",
      description:
        "Use the action menu to delete an exam if it is no longer required.",
      iconClass: "bg-danger-subtle",
    },
    {
      icon: "bi bi-calendar-event-fill",
      title: "Exam Dates",
      description:
        "Start date and end date help schedule the exam period correctly.",
      iconClass: "bg-light",
    },
    {
      icon: "bi bi-file-earmark-arrow-down-fill",
      title: "Download Excel Template",
      description:
        "Download the sample Excel format before bulk uploading exam records.",
      iconClass: "bg-success-subtle",
    },
    {
      icon: "bi bi-file-earmark-arrow-up-fill",
      title: "Bulk Upload Exams",
      description:
        "Upload exam records using Excel to create multiple exams faster.",
      iconClass: "bg-info-subtle",
    },
  ],
  tip: "Use Add New Exam for single entries and Excel upload when creating multiple exams together.",
},

"school-exam-form": {
  title: "Create Exam Walkthrough",
  subtitle:
    "Learn how to fill exam name, exam type, dates, academic year, grace marks, and description.",
  youtubeVideoId: "L84PHS28dTg",
  helpTitle: "How to fill the Create Exam form",
  helpPoints: [
    {
      icon: "bi bi-pencil-square",
      title: "Exam Name",
      description:
        "Enter a clear and meaningful exam name such as Unit Test 1, Mid Term Exam, or Annual Exam.",
      iconClass: "bg-success-subtle",
    },
    {
      icon: "bi bi-list-check",
      title: "Exam Type",
      description:
        "Select exam type from the dropdown. Choose Other only when the exam type is not available.",
      iconClass: "bg-info-subtle",
    },
    {
      icon: "bi bi-calendar-check-fill",
      title: "Start Date",
      description:
        "Select the date from which the exam starts.",
      iconClass: "bg-warning-subtle",
    },
    {
      icon: "bi bi-calendar2-check-fill",
      title: "End Date",
      description:
        "Select the exam end date. End date should be after or equal to start date.",
      iconClass: "bg-light",
    },
    {
      icon: "bi bi-calendar-range-fill",
      title: "Academic Year",
      description:
        "Choose the academic year in which this exam belongs.",
      iconClass: "bg-success-subtle",
    },
    {
      icon: "bi bi-award-fill",
      title: "Grace Marks",
      description:
        "Enter grace marks if the school wants to allow extra marks for this exam.",
      iconClass: "bg-danger-subtle",
    },
    {
      icon: "bi bi-card-text",
      title: "Description",
      description:
        "Add optional exam notes or instructions for internal reference.",
      iconClass: "bg-light",
    },
    {
      icon: "bi bi-check-circle-fill",
      title: "Submit",
      description:
        "After filling required fields, click Submit to save the exam.",
      iconClass: "bg-success-subtle",
    },
  ],
  tip: "Always verify exam name, exam type, start date, end date, and academic year before submitting.",
},

"school-announcement-listing": {
  title: "School Announcements Walkthrough",
  subtitle:
    "Learn how to search, add, edit, delete, schedule, and manage school announcements.",
  youtubeVideoId: "L84PHS28dTg",
  helpTitle: "What this announcement module shows",
  helpPoints: [
    {
      icon: "bi bi-megaphone-fill",
      title: "School Announcements",
      description:
        "Shows all announcements created for the school with title, type, message, audience, created date, post date, and valid until date.",
      iconClass: "bg-success-subtle",
    },
    {
      icon: "bi bi-search",
      title: "Search Announcement",
      description:
        "Use the search box to quickly find announcements by title.",
      iconClass: "bg-info-subtle",
    },
    {
      icon: "bi bi-plus-circle-fill",
      title: "Add Announcement",
      description:
        "Click Add Announcement to open the form and create a new notice or message.",
      iconClass: "bg-warning-subtle",
    },
    {
      icon: "bi bi-tags-fill",
      title: "Announcement Category",
      description:
        "Inside the form, select the announcement category such as Exam, Event, Notice, Reminder, or Other.",
      iconClass: "bg-light",
    },
    {
      icon: "bi bi-chat-left-text-fill",
      title: "Message",
      description:
        "Write the announcement message that should be shown to the selected audience.",
      iconClass: "bg-light",
    },
    {
      icon: "bi bi-translate",
      title: "Language",
      description:
        "Select one or more languages for the announcement message.",
      iconClass: "bg-info-subtle",
    },
    {
      icon: "fa-solid fa-wand-magic-sparkles",
      title: "Generate with AI",
      description:
        "Use Generate with AI to improve, rewrite, or translate the message.",
      iconClass: "bg-danger-subtle",
    },
    {
      icon: "bi bi-people-fill",
      title: "Audience",
      description:
        "Select who should receive the announcement, such as Parent, Student, Teacher, or Staff.",
      iconClass: "bg-success-subtle",
    },
    {
      icon: "bi bi-calendar-event-fill",
      title: "Will be Posted On",
      description:
        "Select the date when this announcement should be posted.",
      iconClass: "bg-warning-subtle",
    },
    {
      icon: "bi bi-calendar-x-fill",
      title: "Valid Until",
      description:
        "Select the last date until which this announcement should remain valid.",
      iconClass: "bg-info-subtle",
    },
    {
      icon: "bi bi-pencil-fill",
      title: "Edit Announcement",
      description:
        "Use the action menu to update an existing announcement.",
      iconClass: "bg-light",
    },
    {
      icon: "bi bi-trash-fill",
      title: "Delete Announcement",
      description:
        "Use the action menu to delete announcements that are no longer required.",
      iconClass: "bg-danger-subtle",
    },
  ],
  tip:
    "Use the single Need Help button on the listing page to understand both the announcement list and Add Announcement form.",
},
  };

  ngOnInit(): void {
    this.applyWalkthroughConfig();
  }

  private applyWalkthroughConfig(): void {
    this.selectedConfig =
      this.walkthroughVideoMap[this._helpKey] ||
      this.walkthroughVideoMap["school-dashboard"];

    this.safeYoutubeUrl = this.getSafeYoutubeEmbedUrl(
      this.selectedConfig.youtubeVideoId,
    );
  }

  private getSafeYoutubeEmbedUrl(videoId: string): SafeResourceUrl {
    const cleanVideoId = (videoId || "").trim();

    const url =
      `https://www.youtube.com/embed/${cleanVideoId}` +
      `?rel=0&modestbranding=1&controls=1&disablekb=1&fs=0&iv_load_policy=3`;

    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  getDebugVideoInfo(): string {
    return `Help Key: ${this.helpKey} | Video ID: ${this.selectedConfig?.youtubeVideoId || "-"}`;
  }

  closeModal(): void {
    this.activeModal.close();
  }
}
